import { useMemo } from 'react'
import {
  BufferGeometry,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  MeshStandardMaterial,
} from 'three'

import { type ObjectMap } from '@react-three/fiber'
import {
  buildChunkedMeshes,
  buildInstancedFromGeo,
  type ChunkConfig,
  type GeoInstance,
} from '@utils'
import { type GLTF } from 'three-stdlib'

type NodeMap = Record<
  string,
  { geometry: BufferGeometry; material: MeshStandardMaterial; matrix?: Matrix4 }
>

/**
 * Builds InstancedMeshes per GLTF variant from a parallel list of spawn instances.
 *
 * `nodeNames` and `getMat` are module-level constants in all callers — they are excluded
 * from deps intentionally. Only `gltfs` and `instances` change at runtime.
 *
 * `applyNodeMatrix` bakes the node's local transform into the geometry so that
 * scale/rotation applied per-instance feel natural (pivot at foot, not geometry center).
 *
 * When `chunkCfg` is provided, each variant is split into spatial chunks — one InstancedMesh
 * per non-empty chunk — enabling per-chunk frustum culling.
 */
export function useInstancedGeoFamily(
  gltfs: (GLTF & ObjectMap)[],
  nodeNames: readonly string[],
  instances: GeoInstance[][],
  getMat: (mat: MeshStandardMaterial) => MeshLambertMaterial,
  applyNodeMatrix = false,
  chunkCfg?: ChunkConfig,
  castShadow = false,
): InstancedMesh[] {
  // nodeNames and getMat are always module-level constants — safe to exclude from deps
  return useMemo(() => {
    const result: InstancedMesh[] = []
    for (let vi = 0; vi < gltfs.length; vi++) {
      const insts = instances[vi]
      if (!insts.length) continue
      const nodes = gltfs[vi].nodes as unknown as NodeMap
      const node = nodes[nodeNames[vi]]
      if (!node?.geometry) continue
      const clonedForMatrix = applyNodeMatrix
      const geo = clonedForMatrix ? node.geometry.clone().applyMatrix4(node.matrix!) : node.geometry
      const mat = getMat(node.material)
      if (chunkCfg) {
        result.push(...buildChunkedMeshes(geo, mat, insts, chunkCfg, undefined, castShadow))
      } else {
        result.push(buildInstancedFromGeo(geo, mat, insts, undefined, castShadow))
      }
      // geo was a fresh clone — each chunk already re-clones it, so dispose the intermediate
      if (clonedForMatrix) geo.dispose()
    }
    return result
  }, [gltfs, instances, applyNodeMatrix, castShadow, getMat, nodeNames, chunkCfg])
}
